import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBarsProgress,
  faCheck,
  faChevronDown,
  faChevronUp,
  faColumns,
  faEdit,
  faEye,
  faEyeSlash,
  faFilter,
  faList,
  faPlus,
  faRotateRight,
  faTriangleExclamation,
  faUserClock,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useWorkspace } from '../context/WorkspaceContext';
import api from '../api/API';
import TaskKanbanView from '../components/TaskKanbanView';
import TaskModal from '../components/Modals/TaskModal';
import { getTaskPriorityLabel, getTaskPriorityTone, normalizeTaskPriority, TASK_PRIORITY_OPTIONS } from '../utils/taskPriority';
import './TarefasGlobalPage.css';

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) throw new Error(response?.data?.message || 'Falha ao carregar dados.');
  return response?.data?.records || response?.data?.data || [];
};

const normalizeText = (value = '') => value.toString().trim().toLowerCase();

const getPriorityLabel = (task) => getTaskPriorityLabel(task?.prioridade ?? task?.prioridade_label ?? task?.prioridade_nome);

const getStatusLabel = (task) => {
  if (task?.concluido) return 'Concluida';
  if (task?.aguardando_terceiros) return 'Aguardando';
  if (task?.id_etapa_nome) return task.id_etapa_nome;
  return 'Em aberto';
};

const formatDate = (value) => {
  if (!value) return 'Sem prazo';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem prazo';

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
};

const getDueBucket = (value) => {
  if (!value) return 'sem-prazo';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const date = new Date(value);
  date.setHours(0, 0, 0, 0);

  if (date < today) return 'atrasada';
  if (date.getTime() === today.getTime()) return 'hoje';

  const weekLimit = new Date(today);
  weekLimit.setDate(weekLimit.getDate() + 7);

  if (date <= weekLimit) return 'semana';
  return 'futuro';
};

const getDueTone = (bucket) => {
  if (bucket === 'atrasada') return 'danger';
  if (bucket === 'hoje') return 'warning';
  if (bucket === 'semana') return 'info';
  return 'secondary';
};

const getCustomFieldValue = (task, field) => {
  const customField = task?.campos_customizados?.find(
    (item) => String(item.id) === String(field.id) || item.chave === field.chave
  );

  return customField?.valor ?? '';
};

const formatCustomFieldValue = (value) => {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Sim' : 'Nao';
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

const TarefasGlobalPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, loading: loadingWorkspaces, error: workspaceError } = useWorkspace();
  const [tarefas, setTarefas] = useState([]);
  const [taskCustomFields, setTaskCustomFields] = useState([]);
  const [taskTags, setTaskTags] = useState([]);
  const [modoVisualizacao, setModoVisualizacao] = useState('lista');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newCustomField, setNewCustomField] = useState({
    nome: '',
    tipo: 'text',
    opcoes: '',
  });
  const [filtros, setFiltros] = useState({
    busca: '',
    projeto: 'all',
    responsavel: 'all',
    prazo: 'all',
    prioridade: 'all',
    status: 'all',
    tag: 'all',
    customFields: {},
  });
  const [ocultarConcluidas, setOcultarConcluidas] = useState(true);
  const [showCustomFieldForm, setShowCustomFieldForm] = useState(false);

  const carregarTarefas = async ({ silencioso = false } = {}) => {
    if (!selectedWorkspaceId) {
      setTarefas([]);
      setLoading(false);
      return;
    }

    // O polling do quadro chama isso a cada poucos segundos - se acionasse o
    // loading de tela cheia toda vez, a pagina inteira "piscaria"
    // regularmente. Atualizacao em segundo plano so troca os dados quando a
    // resposta chega, sem esconder o quadro no meio do caminho.
    if (!silencioso) {
      setLoading(true);
    }
    setErrorMessage('');

    try {
      const response = await api.get(`/tasks?workspaceId=${selectedWorkspaceId}`);
      setTarefas(parseCollection(response));
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
      if (!silencioso) {
        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            'Nao foi possivel carregar as tarefas do workspace.'
        );
      }
    } finally {
      if (!silencioso) {
        setLoading(false);
      }
    }
  };

  const carregarCamposCustomizados = async () => {
    if (!selectedWorkspaceId) {
      setTaskCustomFields([]);
      return;
    }

    try {
      const response = await api.get(`/workspaces/${selectedWorkspaceId}/task-custom-fields`);
      setTaskCustomFields(parseCollection(response));
    } catch (error) {
      console.error('Erro ao carregar campos customizados:', error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          'Nao foi possivel carregar os campos customizados.'
      );
    }
  };

  const carregarTags = async () => {
    if (!selectedWorkspaceId) {
      setTaskTags([]);
      return;
    }

    try {
      const response = await api.get(`/workspaces/${selectedWorkspaceId}/task-tags`);
      setTaskTags(parseCollection(response));
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          'Nao foi possivel carregar as tags.'
      );
    }
  };

  useEffect(() => {
    carregarTarefas();
    carregarCamposCustomizados();
    carregarTags();
  }, [selectedWorkspaceId]);

  // Um agente do pipeline pode mover um card sem ninguem tocar na tela - sem
  // isso, so via ao mudar de coluna quem reabrisse a pagina. Ref pra sempre
  // chamar a versao mais recente de carregarTarefas sem recriar o intervalo a
  // cada render (a funcao nao e useCallback aqui).
  const carregarTarefasRef = useRef(carregarTarefas);
  carregarTarefasRef.current = carregarTarefas;

  useEffect(() => {
    if (!selectedWorkspaceId) return undefined;

    const intervalo = setInterval(() => {
      carregarTarefasRef.current({ silencioso: true });
    }, 8000);

    return () => clearInterval(intervalo);
  }, [selectedWorkspaceId]);

  const criarCampoCustomizado = async () => {
    if (!selectedWorkspaceId || !newCustomField.nome.trim()) return;

    try {
      await api.post(`/workspaces/${selectedWorkspaceId}/task-custom-fields`, {
        nome: newCustomField.nome,
        tipo: newCustomField.tipo,
        opcoes: newCustomField.opcoes,
        ordem: taskCustomFields.length + 1,
      });

      setNewCustomField({ nome: '', tipo: 'text', opcoes: '' });
      await carregarCamposCustomizados();
      await carregarTarefas();
    } catch (error) {
      console.error('Erro ao criar campo customizado:', error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          'Nao foi possivel criar o campo customizado.'
      );
    }
  };

  const projetoOptions = useMemo(() => {
    return Array.from(
      new Map(
        tarefas
          .filter((task) => task.id_projeto && task.projeto)
          .map((task) => [String(task.id_projeto), task.projeto])
      ).entries()
    ).map(([value, label]) => ({ value, label }));
  }, [tarefas]);

  const responsavelOptions = useMemo(() => {
    return Array.from(
      new Map(
        tarefas
          .filter((task) => task.id_usuario_responsavel && (task.responsavel || task.usuario_responsavel))
          .map((task) => [
            String(task.id_usuario_responsavel),
            task.responsavel || task.usuario_responsavel,
          ])
      ).entries()
    ).map(([value, label]) => ({ value, label }));
  }, [tarefas]);

  const tarefasFiltradas = useMemo(() => {
    return tarefas
      .filter((task) => {
        const prioridade = getPriorityLabel(task);
        const status = getStatusLabel(task);
        const prazoBucket = getDueBucket(task.vencimento);
        const searchBlob = [
          task.descricao,
          task.detalhe,
          task.projeto,
          task.responsavel,
          ...(task.tags || []).map((tag) => tag.nome),
          ...(task.campos_customizados || []).map((field) => formatCustomFieldValue(field.valor)),
        ]
          .filter(Boolean)
          .join(' ');

        const matchesCustomFields = taskCustomFields.every((field) => {
          const filterValue = filtros.customFields?.[field.id];
          if (!filterValue) return true;
          const currentValue = formatCustomFieldValue(getCustomFieldValue(task, field));
          return normalizeText(currentValue).includes(normalizeText(filterValue));
        });

        return (
          (!filtros.busca || normalizeText(searchBlob).includes(normalizeText(filtros.busca))) &&
          (filtros.projeto === 'all' || String(task.id_projeto) === filtros.projeto) &&
          (filtros.responsavel === 'all' ||
            (filtros.responsavel === 'sem-dono'
              ? !task.id_usuario_responsavel
              : String(task.id_usuario_responsavel) === filtros.responsavel)) &&
          (filtros.prazo === 'all' || prazoBucket === filtros.prazo) &&
          (filtros.prioridade === 'all' || prioridade === filtros.prioridade) &&
          (filtros.status === 'all' ||
            (filtros.status === 'concluida' ? task.concluido : normalizeText(status).includes(normalizeText(filtros.status)))) &&
          (filtros.tag === 'all' || (task.tags || []).some((tag) => String(tag.id) === filtros.tag)) &&
          matchesCustomFields
        );
      })
      .sort((left, right) => {
        const leftBucket = getDueBucket(left.vencimento);
        const rightBucket = getDueBucket(right.vencimento);
        const bucketWeight = { atrasada: 4, hoje: 3, semana: 2, futuro: 1, 'sem-prazo': 0 };

        const bucketDiff = (bucketWeight[rightBucket] || 0) - (bucketWeight[leftBucket] || 0);
        if (bucketDiff !== 0) return bucketDiff;

        const priorityDiff = normalizeTaskPriority(left.prioridade, 99) - normalizeTaskPriority(right.prioridade, 99);
        if (priorityDiff !== 0) return priorityDiff;

        const leftDate = left.vencimento ? new Date(left.vencimento).getTime() : Number.MAX_SAFE_INTEGER;
        const rightDate = right.vencimento ? new Date(right.vencimento).getTime() : Number.MAX_SAFE_INTEGER;
        return leftDate - rightDate;
      });
  }, [tarefas, filtros, taskCustomFields]);

  const tarefasVisiveis = useMemo(() => {
    if (!ocultarConcluidas) return tarefasFiltradas;
    return tarefasFiltradas.filter((task) => !task.concluido);
  }, [tarefasFiltradas, ocultarConcluidas]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filtros.busca) count++;
    if (filtros.projeto !== 'all') count++;
    if (filtros.responsavel !== 'all') count++;
    if (filtros.prazo !== 'all') count++;
    if (filtros.prioridade !== 'all') count++;
    if (filtros.status !== 'all') count++;
    if (filtros.tag !== 'all') count++;
    Object.values(filtros.customFields || {}).forEach((v) => { if (v) count++; });
    return count;
  }, [filtros]);

  const metricas = useMemo(() => {
    const total = tarefasFiltradas.length;
    const concluidas = tarefasFiltradas.filter((task) => task.concluido).length;
    const atrasadas = tarefasFiltradas.filter((task) => getDueBucket(task.vencimento) === 'atrasada' && !task.concluido).length;
    const hoje = tarefasFiltradas.filter((task) => getDueBucket(task.vencimento) === 'hoje' && !task.concluido).length;
    const semResponsavel = tarefasFiltradas.filter((task) => !task.id_usuario_responsavel).length;
    const progressoPct = total > 0 ? Math.round((concluidas / total) * 100) : 0;

    return { total, concluidas, atrasadas, hoje, semResponsavel, progressoPct };
  }, [tarefasFiltradas]);

  const foco = useMemo(() => {
    if (metricas.atrasadas > 0) return `Voce tem ${metricas.atrasadas} tarefa(s) atrasada(s) pedindo redistribuicao ou destravamento.`;
    if (metricas.hoje > 0) return `${metricas.hoje} entrega(s) vencem hoje. Um bom momento para alinhar responsaveis e dependencias.`;
    if (metricas.semResponsavel > 0) return `${metricas.semResponsavel} tarefa(s) ainda estao sem dono claro no workspace.`;
    return 'Fluxo estabilizado. Use o kanban para redistribuir carga e a lista para priorizar execucao.';
  }, [metricas]);

  const concluir = async (taskId) => {
    setTarefas((prev) => prev.map((t) => (t.id === taskId ? { ...t, concluido: true } : t)));
    try {
      await api.put(`/tasks/${taskId}`, { fields: { concluido: true } });
    } catch (error) {
      setTarefas((prev) => prev.map((t) => (t.id === taskId ? { ...t, concluido: false } : t)));
      console.error('Erro ao concluir tarefa:', error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          'Nao foi possivel concluir a tarefa.'
      );
    }
  };

  const handleTaskMoved = (taskId, newEtapaId) => {
    setTarefas((prev) =>
      prev.map((t) => (String(t.id) === String(taskId) ? { ...t, id_etapa: newEtapaId } : t))
    );
  };

  const limparFiltros = () => {
    setFiltros({
      busca: '',
      projeto: 'all',
      responsavel: 'all',
      prazo: 'all',
      prioridade: 'all',
      status: 'all',
      tag: 'all',
      customFields: {},
    });
  };

  const abrirModalTarefa = (task = null) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const fecharModalTarefa = () => {
    setSelectedTask(null);
    setShowTaskModal(false);
  };

  return (
    <Container fluid className="ops-page">
      <section className="ops-hero">
        <div className="ops-hero-copy">
          <p className="ops-kicker">Workspace Command Center</p>
          <h1>Gestao diaria de execucao</h1>
          <p className="ops-subtitle">
            Uma visao para distribuir, acompanhar e executar tarefas de varios projetos sem perder o pulso do workspace.
          </p>
        </div>

        <div className="ops-hero-aside">
          <div className="ops-workspace-pill">
            <span>Workspace ativo</span>
            <strong>{selectedWorkspace?.nome || 'Nenhum selecionado'}</strong>
          </div>
          <div className="ops-backlog-note">
            Novas tarefas sem etapa devem cair no <strong>Backlog</strong> por padrao neste workspace.
          </div>
        </div>
      </section>

      {workspaceError && <Alert variant="warning">{workspaceError}</Alert>}
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      {!selectedWorkspaceId && !loadingWorkspaces ? (
        <Alert variant="info">
          Selecione um workspace no topo da aplicacao para abrir sua central de operacao.
        </Alert>
      ) : (
        <>
          <Row className="g-3 ops-metrics">
            <Col md={6} xl={2}>
              <Card className="ops-metric-card">
                <Card.Body>
                  <span>Total filtrado</span>
                  <strong>{metricas.total}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card
                className="ops-metric-card danger ops-metric-clickable"
                onClick={() => setFiltros((f) => ({ ...f, prazo: 'atrasada' }))}
                title="Filtrar por atrasadas"
              >
                <Card.Body>
                  <span>Atrasadas</span>
                  <strong>{metricas.atrasadas}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card
                className="ops-metric-card warning ops-metric-clickable"
                onClick={() => setFiltros((f) => ({ ...f, prazo: 'hoje' }))}
                title="Filtrar por vencem hoje"
              >
                <Card.Body>
                  <span>Vencem hoje</span>
                  <strong>{metricas.hoje}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card
                className="ops-metric-card neutral ops-metric-clickable"
                onClick={() => setFiltros((f) => ({ ...f, responsavel: 'sem-dono' }))}
                title="Filtrar por sem responsavel"
              >
                <Card.Body>
                  <span>Sem dono</span>
                  <strong>{metricas.semResponsavel}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card
                className="ops-metric-card success ops-metric-clickable"
                onClick={() => { setFiltros((f) => ({ ...f, status: 'concluida' })); setOcultarConcluidas(false); }}
                title="Filtrar por concluidas"
              >
                <Card.Body>
                  <span>Concluidas</span>
                  <strong>{metricas.concluidas}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="ops-metric-card insight">
                <Card.Body>
                  <span>Progresso</span>
                  <strong>{metricas.progressoPct}%</strong>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="ops-focus-card">
            <Card.Body>
              <div className="ops-focus-icon">
                <FontAwesomeIcon icon={faBarsProgress} />
              </div>
              <div>
                <span className="ops-focus-label">Leitura do dia</span>
                <p className="mb-0">{foco}</p>
              </div>
            </Card.Body>
          </Card>

          <Card className="ops-toolbar">
            <Card.Body>
              <div className="ops-toolbar-top">
                <div>
                  <h5 className="mb-1">Painel operacional</h5>
                  <p className="text-muted mb-0">
                    Combine filtros de prioridade, prazo e responsavel para organizar carga e redistribuir trabalho.
                  </p>
                </div>

                <div className="ops-toolbar-right">
                  <Button
                    variant={ocultarConcluidas ? 'outline-secondary' : 'secondary'}
                    onClick={() => setOcultarConcluidas((v) => !v)}
                    title={ocultarConcluidas ? 'Exibir concluidas' : 'Ocultar concluidas'}
                  >
                    <FontAwesomeIcon icon={ocultarConcluidas ? faEyeSlash : faEye} />
                    {' '}{ocultarConcluidas ? 'Concluidas ocultas' : 'Concluidas visiveis'}
                  </Button>

                  <div className="ops-view-toggle">
                    <Button
                      variant={modoVisualizacao === 'lista' ? 'dark' : 'outline-dark'}
                      onClick={() => setModoVisualizacao('lista')}
                    >
                      <FontAwesomeIcon icon={faList} /> Lista
                    </Button>
                    <Button
                      variant={modoVisualizacao === 'kanban' ? 'dark' : 'outline-dark'}
                      onClick={() => setModoVisualizacao('kanban')}
                    >
                      <FontAwesomeIcon icon={faColumns} /> Kanban
                    </Button>
                  </div>

                  <Button variant="dark" onClick={() => abrirModalTarefa(null)}>
                    <FontAwesomeIcon icon={faPlus} /> Nova tarefa
                  </Button>
                </div>
              </div>

              <div className="ops-filters-grid">
                <Form.Control
                  placeholder="Buscar por tarefa, detalhe, projeto ou responsavel"
                  value={filtros.busca}
                  onChange={(event) => setFiltros((current) => ({ ...current, busca: event.target.value }))}
                />

                <Form.Select
                  value={filtros.projeto}
                  onChange={(event) => setFiltros((current) => ({ ...current, projeto: event.target.value }))}
                >
                  <option value="all">Todos os projetos</option>
                  {projetoOptions.map((project) => (
                    <option key={project.value} value={project.value}>
                      {project.label}
                    </option>
                  ))}
                </Form.Select>

                <Form.Select
                  value={filtros.responsavel}
                  onChange={(event) => setFiltros((current) => ({ ...current, responsavel: event.target.value }))}
                >
                  <option value="all">Todos os responsaveis</option>
                  <option value="sem-dono">Sem responsavel</option>
                  {responsavelOptions.map((member) => (
                    <option key={member.value} value={member.value}>
                      {member.label}
                    </option>
                  ))}
                </Form.Select>

                <Form.Select
                  value={filtros.prazo}
                  onChange={(event) => setFiltros((current) => ({ ...current, prazo: event.target.value }))}
                >
                  <option value="all">Todo horizonte</option>
                  <option value="atrasada">Atrasadas</option>
                  <option value="hoje">Hoje</option>
                  <option value="semana">Proxima semana</option>
                  <option value="futuro">Futuro</option>
                  <option value="sem-prazo">Sem prazo</option>
                </Form.Select>

                <Form.Select
                  value={filtros.prioridade}
                  onChange={(event) => setFiltros((current) => ({ ...current, prioridade: event.target.value }))}
                >
                  <option value="all">Toda prioridade</option>
                  {TASK_PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.label}>{option.label}</option>
                  ))}
                  <option value="Sem prioridade">Sem prioridade</option>
                </Form.Select>

                <Form.Select
                  value={filtros.status}
                  onChange={(event) => setFiltros((current) => ({ ...current, status: event.target.value }))}
                >
                  <option value="all">Todo status</option>
                  <option value="concluida">Concluidas</option>
                  <option value="aberto">Em aberto</option>
                  <option value="aguardando">Aguardando</option>
                  <option value="backlog">Backlog</option>
                </Form.Select>

                <Form.Select
                  value={filtros.tag}
                  onChange={(event) => setFiltros((current) => ({ ...current, tag: event.target.value }))}
                >
                  <option value="all">Todas as tags</option>
                  {taskTags.map((tag) => (
                    <option key={tag.id} value={String(tag.id)}>
                      {tag.nome}
                    </option>
                  ))}
                </Form.Select>

                <div className="ops-filter-actions">
                  <Button variant="outline-secondary" onClick={limparFiltros}>
                    <FontAwesomeIcon icon={faFilter} /> Limpar
                    {activeFilterCount > 0 && (
                      <Badge bg="dark" className="ms-1">{activeFilterCount}</Badge>
                    )}
                  </Button>
                  <Button variant="outline-dark" onClick={carregarTarefas}>
                    <FontAwesomeIcon icon={faRotateRight} /> Atualizar
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="ops-toolbar">
            <Card.Body>
              <div className="ops-toolbar-top">
                <div className="ops-custom-fields-header">
                  <h5 className="mb-0">Campos customizados</h5>
                  {taskCustomFields.length > 0 && (
                    <div className="ops-custom-field-list mt-2">
                      {taskCustomFields.map((field) => (
                        <Badge key={field.id} bg="secondary">
                          {field.nome}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  variant={showCustomFieldForm ? 'secondary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setShowCustomFieldForm((v) => !v)}
                >
                  <FontAwesomeIcon icon={showCustomFieldForm ? faChevronUp : faChevronDown} />
                  {' '}{showCustomFieldForm ? 'Fechar' : 'Novo campo'}
                </Button>
              </div>

              {showCustomFieldForm && (
                <div className="ops-custom-fields-grid mt-3">
                  <Form.Control
                    placeholder="Nome do campo"
                    value={newCustomField.nome}
                    onChange={(event) => setNewCustomField((current) => ({ ...current, nome: event.target.value }))}
                  />
                  <Form.Select
                    value={newCustomField.tipo}
                    onChange={(event) => setNewCustomField((current) => ({ ...current, tipo: event.target.value }))}
                  >
                    <option value="text">Texto</option>
                    <option value="number">Numero</option>
                    <option value="date">Data</option>
                    <option value="boolean">Sim/Nao</option>
                    <option value="select">Lista</option>
                    <option value="multi_select">Lista multipla</option>
                    <option value="user">Usuario</option>
                    <option value="url">URL</option>
                  </Form.Select>
                  <Form.Control
                    placeholder="Opcoes separadas por virgula (apenas para Lista)"
                    value={newCustomField.opcoes}
                    disabled={!['select', 'multi_select'].includes(newCustomField.tipo)}
                    onChange={(event) => setNewCustomField((current) => ({ ...current, opcoes: event.target.value }))}
                  />
                  <Button variant="dark" onClick={criarCampoCustomizado}>
                    <FontAwesomeIcon icon={faPlus} /> Criar campo
                  </Button>
                </div>
              )}

              {taskCustomFields.length > 0 && (
                <div className="ops-custom-filter-grid mt-3">
                  {taskCustomFields.map((field) => (
                    <Form.Control
                      key={field.id}
                      placeholder={`Filtrar por ${field.nome}`}
                      value={filtros.customFields?.[field.id] || ''}
                      onChange={(event) =>
                        setFiltros((current) => ({
                          ...current,
                          customFields: {
                            ...current.customFields,
                            [field.id]: event.target.value,
                          },
                        }))
                      }
                    />
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          {loading ? (
            <div className="text-center mt-5">
              <Spinner animation="border" variant="dark" />
            </div>
          ) : modoVisualizacao === 'lista' ? (
            <section className="ops-list-shell">
              <div className="ops-list-header">
                <div>
                  <h5>Fila de decisao</h5>
                  {ocultarConcluidas && metricas.concluidas > 0 && (
                    <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                      {metricas.concluidas} concluida(s) oculta(s)
                    </p>
                  )}
                </div>
                <Badge bg="dark">{tarefasVisiveis.length} tarefas</Badge>
              </div>

              {tarefasVisiveis.length === 0 ? (
                <div className="ops-empty-state">
                  Nenhuma tarefa encontrada com os filtros atuais.
                </div>
              ) : (
                <div className="ops-task-list">
                  {tarefasVisiveis.map((task) => {
                    const dueBucket = getDueBucket(task.vencimento);
                    const priorityLabel = getPriorityLabel(task);
                    const statusLabel = getStatusLabel(task);

                    return (
                      <article key={task.id} className="ops-task-row">
                        <div className="ops-task-main">
                          <div className="ops-task-title-line">
                            <h6>{task.descricao}</h6>
                            <div className="ops-task-badges">
                              <Badge bg={getDueTone(dueBucket)}>
                                {dueBucket === 'atrasada'
                                  ? 'Atrasada'
                                  : dueBucket === 'hoje'
                                    ? 'Hoje'
                                    : dueBucket === 'semana'
                                      ? 'Semana'
                                      : dueBucket === 'sem-prazo'
                                        ? 'Sem prazo'
                                        : 'Futuro'}
                              </Badge>
                              <Badge bg={getTaskPriorityTone(task.prioridade)}>{priorityLabel}</Badge>
                              <Badge bg={task.concluido ? 'success' : 'secondary'}>{statusLabel}</Badge>
                            </div>
                          </div>

                          {task.detalhe && (
                            <p className="ops-task-detail">{task.detalhe}</p>
                          )}

                          <div className="ops-task-meta">
                            <span>
                              <FontAwesomeIcon icon={faBarsProgress} /> {task.projeto || 'Sem projeto'}
                            </span>
                            <span>
                              <FontAwesomeIcon icon={faUserClock} /> {task.responsavel || 'Sem responsavel'}
                            </span>
                            <span>
                              <FontAwesomeIcon icon={faTriangleExclamation} /> {formatDate(task.vencimento)}
                            </span>
                          </div>

                          {task.tags?.length > 0 && (
                            <div className="ops-task-tags">
                              {task.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="ops-tag-pill"
                                  style={{
                                    backgroundColor: tag.cor_hexa || '#e2e8f0',
                                    color: tag.cor_texto_hexa || '#0f172a',
                                  }}
                                >
                                  {tag.nome}
                                </span>
                              ))}
                            </div>
                          )}

                          {task.campos_customizados?.some((field) => field.valor !== null && field.valor !== undefined && field.valor !== '') && (
                            <div className="ops-task-custom-fields">
                              {task.campos_customizados
                                .filter((field) => field.valor !== null && field.valor !== undefined && field.valor !== '')
                                .map((field) => (
                                  <Badge key={field.id} bg="light" text="dark">
                                    {field.nome}: {formatCustomFieldValue(field.valor)}
                                  </Badge>
                                ))}
                            </div>
                          )}
                        </div>

                        <div className="ops-task-actions">
                          <Button size="sm" variant="outline-secondary" onClick={() => abrirModalTarefa(task)}>
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          {!task.concluido && (
                            <Button size="sm" variant="success" onClick={() => concluir(task.id)}>
                              <FontAwesomeIcon icon={faCheck} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline-dark"
                            onClick={() => navigate(`/projetos/${task.id_projeto}`)}
                          >
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                          </Button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          ) : (
            <TaskKanbanView
              user={user}
              workspaceId={selectedWorkspaceId}
              tarefas={tarefasVisiveis}
              onTaskMoved={handleTaskMoved}
              onTaskClick={abrirModalTarefa}
            />
          )}
        </>
      )}

      <TaskModal
        show={showTaskModal}
        onClose={fecharModalTarefa}
        task={selectedTask}
        allowInboxTask
        onSaved={() => {
          fecharModalTarefa();
          carregarTarefas();
          carregarTags();
        }}
      />
    </Container>
  );
};

export default TarefasGlobalPage;
